import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useModules } from '../contexts/ModuleContext';
import api from '../services/api';
import { showError, showSuccess } from '../services/alert';
import { ROLE_GROUPS, canAccessModulePath, getUserRoles, hasAnyRole } from '../utils/permissions';
import './Header.css';

const getGreeting = (date) => {
  const hour = date.getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
};

const searchItems = [
  { label: 'Painel', description: 'Resumo executivo da fazenda', path: '/menu', icon: 'bi-speedometer2', roles: ROLE_GROUPS.todosTenant },
  { label: 'Primeiros passos', description: 'Configure sua fazenda e comece a usar o sistema', path: '/onboarding-inicial', icon: 'bi-list-check', roles: ROLE_GROUPS.administracao },
  { label: 'Fazendas', description: 'Cadastro de propriedades', path: '/propriedades', icon: 'bi-house-check', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Talhões', description: 'Áreas produtivas', path: '/talhoes', icon: 'bi-grid-1x2', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Safras', description: 'Ciclos de cultivo', path: '/safras', icon: 'bi-calendar4-week', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Plantio', description: 'Plantio por safra e talhão', path: '/plantios', icon: 'bi-flower1', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Fluxo do Plantio', description: 'Preparações de solo, aplicações e lembretes', path: '/fluxo-plantio', icon: 'bi-signpost-split', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Insumos', description: 'Estoque e cadastro de insumos', path: '/insumos', icon: 'bi-box-seam', roles: ROLE_GROUPS.operacional },
  { label: 'Compra de Insumos', description: 'Entrada, estoque e financeiro', path: '/compras-insumos', icon: 'bi-cart-plus', roles: ROLE_GROUPS.operacional },
  { label: 'Aplicações no Campo', description: 'Baixa de insumos no campo', path: '/aplicacoesinsumo', icon: 'bi-droplet', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Operações de Campo', description: 'Atividades e custo operacional', path: '/operacoes-campo', icon: 'bi-journal-text', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Colheita', description: 'Registro de colheitas', path: '/colheitas', icon: 'bi-basket-fill', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Armazém e Venda', description: 'Produção armazenada e vendas', path: '/producao-agricola', icon: 'bi-boxes', roles: ROLE_GROUPS.operacionalAgricultor },
  { label: 'Controle Financeiro Agro', description: 'Caixa, livro caixa e lucro', path: '/financeiro/controle-agro', icon: 'bi-graph-up-arrow', roles: ROLE_GROUPS.financeiro },
  { label: 'Exportação bancária', description: 'Gerar CNAB240', path: '/financeiro/exportacao-bancaria', icon: 'bi-bank', roles: ROLE_GROUPS.financeiro },
  { label: 'Fechamento da Safra', description: 'Resultado por safra e talhão', path: '/fechamento-safra', icon: 'bi-clipboard-data', roles: ROLE_GROUPS.relatoriosOperacionais },
  { label: 'Nota Fiscal', description: 'NF-e e documentos fiscais', path: '/notas-fiscais', icon: 'bi-receipt', roles: ROLE_GROUPS.fiscal },
  { label: 'CT-e / Frete', description: 'Frete agro e transporte', path: '/cte', icon: 'bi-truck', roles: ROLE_GROUPS.financeiroOperacional },
  { label: 'Certificado Digital', description: 'Configuração fiscal', path: '/configuracoes/certificado', icon: 'bi-shield-lock', roles: ROLE_GROUPS.fiscal },
  { label: 'Relatórios', description: 'Análises integradas', path: '/relatorios/integrado', icon: 'bi-bar-chart-line', roles: ROLE_GROUPS.financeiroOperacional },
];

const notifications = [
  {
    title: 'Verificar estoque crítico',
    description: 'Confira itens com saldo baixo antes de novas operações.',
    path: '/insumos',
    tone: 'danger',
    icon: 'bi-exclamation-triangle',
    roles: ROLE_GROUPS.operacional
  },
  {
    title: 'Fechar safra pendente',
    description: 'Revise receitas, despesas e lucro por talhão.',
    path: '/fechamento-safra',
    tone: 'warning',
    icon: 'bi-clipboard-data',
    roles: ROLE_GROUPS.relatoriosOperacionais
  },
  {
    title: 'Exportação bancária pronta',
    description: 'Selecione despesas pendentes e gere CNAB240.',
    path: '/financeiro/exportacao-bancaria',
    tone: 'info',
    icon: 'bi-bank',
    roles: ROLE_GROUPS.financeiro
  }
];

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getNameFromToken = (token) => {
  if (!token) return '';

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || payload.name || '';
  } catch {
    return '';
  }
};

const getFirstName = (name) => {
  const trimmed = String(name || '').trim();
  return trimmed ? trimmed.split(/\s+/)[0] : 'usuário';
};

const Header = () => {
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useContext(ThemeContext);
  const { tenant, setTenant, tenants, tenantsLoading, tenantsError } = useTenant();
  const { token } = useContext(AuthContext);
  const { modules } = useModules();
  const [now, setNow] = useState(new Date());
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [flowReminders, setFlowReminders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoLoadFailed, setPhotoLoadFailed] = useState(false);
  const photoInputRef = useRef(null);
  const searchRef = useRef(null);
  const notificationsRef = useRef(null);
  const isAuthenticated = Boolean(token);
  const userRoles = useMemo(() => getUserRoles(token), [token]);
  const isPlatformAdmin = userRoles.includes('PlatformAdmin');

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const load = () => {
      const saved = JSON.parse(localStorage.getItem('cultivamais.fluxo-plantio.avaliacao') || '[]');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      setFlowReminders(saved.filter(item => {
        const dataAlerta = item.dataPrevista || item.data;
        if (!dataAlerta) return false;
        if (['Realizada', 'Cancelada'].includes(item.status)) return false;
        const due = new Date(`${dataAlerta}T00:00:00`);
        return Math.ceil((due - today) / 86400000) <= 5;
      }).map(item => { const dataAlerta=item.dataPrevista||item.data; return ({ title: `${item.etapa || 'Próxima aplicação'} · ${item.talhao || 'Talhão'}`, description: `${dataAlerta.split('-').reverse().join('/')} ${item.produto ? `• ${item.produto}` : ''}`, path: '/fluxo-plantio', tone: new Date(`${dataAlerta}T00:00:00`) < today ? 'danger' : 'warning', icon: 'bi-calendar-event' }); }));
    };
    load();
    window.addEventListener('storage', load);
    window.addEventListener('cultivamais:lembretes-atualizados', load);
    return () => { window.removeEventListener('storage', load); window.removeEventListener('cultivamais:lembretes-atualizados', load); };
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }

      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!token) {
      setProfile(null);
      return;
    }

    let active = true;

    api.get('/perfil/me')
      .then((response) => {
        if (active) {
          setProfile(response.data);
          setPhotoLoadFailed(false);
        }
      })
      .catch(() => {
        if (active) {
          setProfile(null);
        }
      });

    return () => {
      active = false;
    };
  }, [token]);

  const tenantOptions = useMemo(() => {
    if (!Array.isArray(tenants)) {
      return [];
    }

    return tenants.map((item) => ({
      id: item.id,
      name: item.name ?? item.nome ?? item.id,
    }));
  }, [tenants]);

  const selectedTenantName = useMemo(() => {
    const selected = tenantOptions.find((option) => option.id === tenant);
    return selected?.name ?? tenant ?? '';
  }, [tenantOptions, tenant]);

  const showTenantSelector = tenantOptions.length > 1 || tenantsLoading || Boolean(tenantsError);

  const searchResults = useMemo(() => {
    const term = normalize(search);
    const allowedItems = searchItems.filter((item) => hasAnyRole(userRoles, item.roles) && canAccessModulePath(item.path, modules));
    if (!term) return allowedItems.slice(0, 6);
    return allowedItems
      .filter((item) => normalize(`${item.label} ${item.description}`).includes(term))
      .slice(0, 8);
  }, [search, userRoles, modules]);

  const allowedNotifications = useMemo(() => [
    ...flowReminders,
    ...notifications.filter((item) => hasAnyRole(userRoles, item.roles) && canAccessModulePath(item.path, modules))
  ], [userRoles, modules, flowReminders]);

  const openRoute = (path) => {
    setSearch('');
    setSearchOpen(false);
    setNotificationsOpen(false);
    navigate(path);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const first = searchResults[0];
    if (first) {
      openRoute(first.path);
    }
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showError('Envie uma imagem JPG, PNG ou WEBP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError('A foto deve ter no maximo 5 MB.');
      return;
    }

    const formData = new FormData();
    formData.append('arquivo', file);

    try {
      setUploadingPhoto(true);
      const response = await api.post('/perfil/foto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfile((current) => ({
        ...(current || {}),
        fotoPerfilUrl: response.data.fotoPerfilUrl,
        fotoPerfilUrlAbsoluta: response.data.fotoPerfilUrlAbsoluta
          ? `${response.data.fotoPerfilUrlAbsoluta}?v=${Date.now()}`
          : null
      }));
      setPhotoLoadFailed(false);
      showSuccess('Foto de perfil atualizada.');
    } catch (error) {
      showError(error.response?.data?.message || 'Não foi possível atualizar a foto.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const profileInitial = (profile?.nome || 'A').trim().charAt(0).toUpperCase() || 'A';
  const profilePhoto = photoLoadFailed ? null : profile?.fotoPerfilUrlAbsoluta;
  const userName = getFirstName(profile?.nome || getNameFromToken(token));

  return (
    <header className="topbar-premium">
      <div className="topbar-greeting">
        <span className="topbar-eyebrow">{now.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' })}</span>
        <h1>{getGreeting(now)}, {userName}.</h1>
        <p>{isPlatformAdmin ? 'Controle de clientes, assinaturas e operação da plataforma.' : 'Visão executiva da fazenda, operação e financeiro em tempo real.'}</p>
      </div>

      <div className="topbar-actions">
        <form className="topbar-search-wrap" onSubmit={handleSearchSubmit} ref={searchRef}>
          <label className="topbar-search">
            <i className="bi bi-search" />
            <input
              type="search"
              placeholder="Buscar no Cultiva Mais"
              aria-label="Buscar no Cultiva Mais"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSearchOpen(true);
                setNotificationsOpen(false);
              }}
              onFocus={() => {
                setSearchOpen(true);
                setNotificationsOpen(false);
              }}
            />
          </label>

          {searchOpen && (
            <div className="topbar-popover topbar-search-results">
              <div className="topbar-popover-title">Busca rápida</div>
              {searchResults.map((item) => (
                <button type="button" className="topbar-result-item" key={item.path} onMouseDown={() => openRoute(item.path)}>
                  <i className={`bi ${item.icon}`} />
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.description}</small>
                  </span>
                </button>
              ))}
              {!searchResults.length && (
                <div className="topbar-empty">Nenhuma tela encontrada.</div>
              )}
            </div>
          )}
        </form>

        {showTenantSelector && (
          <div className="topbar-tenant">
            <span>Cliente</span>

            {isAuthenticated ? (
              <strong>{selectedTenantName || 'Cliente não identificado'}</strong>
            ) : (
              <select
                value={tenant}
                onChange={(event) => setTenant(event.target.value)}
                disabled={tenantsLoading}
              >
                <option value="">
                  {tenantsLoading ? 'Carregando clientes...' : 'Selecione um cliente'}
                </option>
                {tenantOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            )}

            {tenantsError && <small>{tenantsError}</small>}
          </div>
        )}

        <div className="topbar-notifications" ref={notificationsRef}>
          <button
            className="topbar-icon-button"
            type="button"
            title="Notificações"
            aria-label="Notificações"
            aria-expanded={notificationsOpen}
            onClick={() => {
              setNotificationsOpen((current) => !current);
              setSearchOpen(false);
            }}
          >
            <i className="bi bi-bell" />
            {allowedNotifications.length > 0 && <span className="topbar-notification-count">{allowedNotifications.length}</span>}
          </button>

          {notificationsOpen && (
            <div className="topbar-popover topbar-notification-panel">
              <div className="topbar-popover-title">Alertas operacionais</div>
              {allowedNotifications.map((item) => (
                <button type="button" className={`topbar-notification-item topbar-alert-${item.tone}`} key={item.title} onClick={() => openRoute(item.path)}>
                  <i className={`bi ${item.icon}`} />
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </span>
                </button>
              ))}
              {!allowedNotifications.length && (
                <div className="topbar-empty">Nenhum alerta para o seu perfil.</div>
              )}
            </div>
          )}
        </div>

        <button
          className="topbar-theme-button"
          onClick={toggleTheme}
          title="Alternar tema"
          type="button"
        >
          <i className={`bi ${darkMode ? 'bi-sun' : 'bi-moon-stars'}`} />
          <span>{darkMode ? 'Modo Claro' : 'Modo Escuro'}</span>
        </button>

        <button
          className={`topbar-avatar ${profilePhoto ? 'has-photo' : ''}`}
          type="button"
          title="Alterar foto do perfil"
          aria-label="Alterar foto do perfil"
          onClick={() => photoInputRef.current?.click()}
          disabled={uploadingPhoto}
        >
          {profilePhoto ? (
            <img
              src={profilePhoto}
              alt=""
              onError={() => setPhotoLoadFailed(true)}
            />
          ) : (
            profileInitial
          )}
          {uploadingPhoto && <span className="topbar-avatar-loading" />}
        </button>
        <input
          ref={photoInputRef}
          className="topbar-avatar-input"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handlePhotoChange}
        />
      </div>
    </header>
  );
};

export default Header;
